import React from "react";
import { ShieldCheck } from "lucide-react";
import { accentColor } from "@/components/Holo";

// Identity-preservation controls (Phase 1). Mirrors backend ai_engine option vocab.
export const IDENTITY_DEFAULTS = {
  facial_softness: "natural",
  age_preservation: "preserve_exact",
  identity_strength: "balanced",
  subject: "auto",
};

const CONTROLS = [
  {
    key: "identity_strength", label: "Identity Strength",
    options: [["creative", "Creative"], ["balanced", "Balanced"], ["strict", "Strict"], ["maximum", "Max Preserve"]],
  },
  {
    key: "facial_softness", label: "Facial Softness",
    options: [["soft", "Soft"], ["natural", "Natural"], ["defined", "Defined"], ["strong", "Strong"]],
  },
  {
    key: "age_preservation", label: "Age Preservation",
    options: [["preserve_exact", "Preserve Exact"], ["soften_skin", "Soften Skin"], ["natural_mature", "Natural Mature"], ["editorial", "Editorial"], ["no_modification", "No Change"]],
  },
  {
    key: "subject", label: "Subject",
    options: [["auto", "Auto"], ["female", "Female"], ["male", "Male"], ["non_binary", "Non-binary"]],
  },
];

const Segmented = ({ label, options, value, onChange, hex, rgb, testid }) => (
  <div data-testid={testid}>
    <span className="font-mono-tech text-[9px] uppercase tracking-widest text-white/40">{label}</span>
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {options.map(([val, lbl]) => {
        const active = value === val;
        return (
          <button
            key={val}
            type="button"
            onClick={() => onChange(val)}
            data-testid={`${testid}-${val}`}
            className="font-mono-tech text-[10px] uppercase tracking-wider px-2 py-1 border transition-colors"
            style={active
              ? { borderColor: hex, color: hex, boxShadow: `0 0 10px rgba(${rgb},0.3)` }
              : { borderColor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.5)" }}
          >
            {lbl}
          </button>
        );
      })}
    </div>
  </div>
);

export function IdentityControls({ value, onChange, accent = "turquoise", compact = false, testid = "identity-controls" }) {
  const c = accentColor(accent);
  const v = { ...IDENTITY_DEFAULTS, ...(value || {}) };
  const set = (key, val) => onChange({ ...v, [key]: val });

  return (
    <div data-testid={testid} className="space-y-4">
      <div className="flex items-center gap-1.5">
        <ShieldCheck size={12} style={{ color: c.hex }} />
        <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em]" style={{ color: c.hex }}>
          Identity Preservation
        </span>
      </div>
      <div className={compact ? "space-y-3" : "grid sm:grid-cols-2 gap-4"}>
        {CONTROLS.map((ctl) => (
          <Segmented
            key={ctl.key}
            label={ctl.label}
            options={ctl.options}
            value={v[ctl.key]}
            onChange={(val) => set(ctl.key, val)}
            hex={c.hex}
            rgb={c.rgb}
            testid={`${testid}-${ctl.key}`}
          />
        ))}
      </div>
      <p className="font-mono-tech text-[9px] text-white/30 leading-relaxed">
        Higher preservation keeps facial geometry, age & skin tone true to your reference. Guards against masculine drift, over-aging and identity blending.
      </p>
    </div>
  );
}

export default IdentityControls;
